/**
 * InventoryCheck — Buyer-facing page listing all inventory-check requests.
 *
 * Flow:
 * 1. Fetches paginated list from GET /checkout/inventory-check-requests/list/
 * 2. Renders each request as InventoryCheckCard showing status, quotes, seller response.
 * 3. For approved requests with an order (invoice), embeds InvoiceDetails on the right
 *    so the buyer can fill shipping info and accept the invoice.
 * 4. On successful accept, InvoiceDetails handles redirect to /invoices for payment.
 *
 * Route: /inventory-check-requests (protected, buyer only)
 */

import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import InvoiceDetails from "./InvoiceDetails";

import { privateApi } from "../../api/axiosConfig";

const STATUS_CONFIG = {
  pending: {
    label: "در انتظار بررسی",
    color: "warning",
  },
  approved: {
    label: "تأیید شده",
    color: "success",
  },
  rejected: {
    label: "رد شده",
    color: "error",
  },
  declined: {
    label: "رد شده",
    color: "error",
  },
  expired: {
    label: "منقضی شده",
    color: "default",
  },
  cancelled: {
    label: "لغو شده",
    color: "default",
  },
};

const formatNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  const number = Number(value);

  return Number.isFinite(number)
    ? new Intl.NumberFormat("fa-IR").format(number)
    : String(value);
};

const formatDate = (value) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("fa-IR", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

function RequestStatus({ status }) {
  const config = STATUS_CONFIG[status] || {
    label: status || "نامشخص",
    color: "default",
  };

  return (
    <Chip
      label={config.label}
      color={config.color}
      size="small"
      variant={status === "pending" ? "outlined" : "filled"}
    />
  );
}

function QuoteDetails({ quote }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: "action.hover",
      }}
    >
      <Typography fontWeight={700} mb={1}>
        جزئیات درخواست کالا
      </Typography>

      <Stack spacing={1}>
        <DetailRow
          label="تعداد بسته تأییدشده"
          value={formatNumber(quote.confirmed_package_quantity)}
        />

        <DetailRow
          label="قیمت واحد تأییدشده"
          value={
            quote.confirmed_unit_price != null
              ? `${formatNumber(quote.confirmed_unit_price)} تومان`
              : "هنوز تعیین نشده"
          }
        />
      </Stack>
    </Box>
  );
}

function DetailRow({ label, value }) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="flex-start"
      spacing={2}
    >
      <Typography color="text.secondary" variant="body2">
        {label}
      </Typography>

      <Typography
        variant="body2"
        fontWeight={500}
        textAlign="left"
        sx={{ overflowWrap: "anywhere" }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

function InventoryCheckCard({ request }) {
  const canViewOrder = request.order !== null && request.order !== undefined;

  const showInvoice = request.status === "approved" && canViewOrder;

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        overflow: "hidden",
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        {/* Request header spans both columns */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={1.5}
          mb={2}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              درخواست شماره {formatNumber(request.id)}
            </Typography>

            <Typography variant="body2" color="text.secondary" mt={0.5}>
              ثبت‌شده در {formatDate(request.created_at)}
            </Typography>
          </Box>

          <RequestStatus status={request.status} />
        </Stack>

        <Divider sx={{ mb: 2.5 }} />

        {/* LTR grid controls physical left/right placement.
              Individual sections remain RTL. */}
        <Box
          sx={{
            display: "grid",
            direction: "ltr",
            gridTemplateColumns: showInvoice
              ? {
                  xs: "minmax(0, 1fr)",
                  md: "minmax(0, 1fr) minmax(0, 1fr)",
                }
              : "minmax(0, 1fr)",
            gridTemplateAreas: showInvoice
              ? {
                  xs: '"request" "invoice"',
                  md: '"request invoice"',
                }
              : '"request"',
            gap: { xs: 2.5, md: 3 },
            alignItems: "start",
          }}
        >
          {/* LEFT: Inventory request and quote */}
          <Box
            sx={{
              gridArea: "request",
              direction: "rtl",
              minWidth: 0,
            }}
          >
            {canViewOrder && (
              <Box
                sx={{
                  p: 1.5,
                  mb: 2,
                  borderRadius: 2,
                  bgcolor: "action.hover",
                }}
              >
                <DetailRow
                  label="شماره سفارش"
                  value={formatNumber(request.order)}
                />
              </Box>
            )}

            {request.seller_response?.trim() && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography fontWeight={700} mb={0.5}>
                  پاسخ فروشنده
                </Typography>
                {request.seller_response}
              </Alert>
            )}

            <Typography fontWeight={700} mb={1.5}>
              کالاهای درخواست‌شده
            </Typography>

            {Array.isArray(request.quotes) && request.quotes.length > 0 ? (
              <Stack spacing={1.5}>
                {request.quotes.map((quote) => (
                  <QuoteDetails key={quote.id} quote={quote} />
                ))}
              </Stack>
            ) : (
              <Alert severity="info">
                جزئیات کالا برای این درخواست موجود نیست.
              </Alert>
            )}
          </Box>

          {/* RIGHT: Invoice and shipping form */}
          {showInvoice && (
            <Box
              sx={{
                gridArea: "invoice",
                direction: "rtl",
                minWidth: 0,
                borderTop: {
                  xs: 1,
                  md: 0,
                },
                borderColor: "divider",
                pt: {
                  xs: 2,
                  md: 0,
                },
              }}
            >
              <InvoiceDetails invoiceId={request.order} />
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

export default function InventoryCheck() {
  const [requests, setRequests] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await privateApi.get(
        "/api/v1/checkout/inventory-check-requests/list/",
        {
          params: { page },
        },
      );

      const data = response.data;

      setRequests(Array.isArray(data.results) ? data.results : []);
      setCount(Number(data.count) || 0);
      setHasNext(Boolean(data.next));
      setHasPrevious(Boolean(data.previous));
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          err.response?.data?.message ||
          "دریافت درخواست‌های بررسی موجودی با خطا مواجه شد.",
      );
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    document.title = "استعلام موجودی";
    window.scrollTo({top:0, behavior: "smooth"})
    fetchRequests();
  }, [fetchRequests]);

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 } }} dir="rtl">
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
        mb={3}
      >
        <Box>
          <Typography variant="h5" fontWeight={800}>
            درخواست‌های بررسی موجودی
          </Typography>

          <Typography variant="body2" color="text.secondary" mt={1}>
            تعداد کل درخواست‌ها: {formatNumber(count)}
          </Typography>
        </Box>

        <Button
          variant="outlined"
          onClick={fetchRequests}
          disabled={loading}
          startIcon={<RefreshIcon />}
          sx={{ whiteSpace: "nowrap", gap: 1 }}
        >
          بروزرسانی
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : requests.length === 0 && !error ? (
        <Alert severity="info">
          هنوز هیچ درخواست بررسی موجودی ثبت نکرده‌اید.
        </Alert>
      ) : (
        <Stack spacing={2}>
          {requests.map((request) => (
            <InventoryCheckCard key={request.id} request={request} />
          ))}
        </Stack>
      )}

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mt={3}
      >
        <Button
          variant="outlined"
          startIcon={<ArrowForwardIcon />}
          disabled={!hasPrevious || loading}
          onClick={() => setPage((current) => Math.max(1, current - 1))}
        >
          صفحه قبل
        </Button>

        <Typography variant="body2" color="text.secondary">
          صفحه {formatNumber(page)}
        </Typography>

        <Button
          variant="outlined"
          endIcon={<ArrowBackIcon />}
          disabled={!hasNext || loading}
          onClick={() => setPage((current) => current + 1)}
        >
          صفحه بعد
        </Button>
      </Stack>
    </Container>
  );
}
